/* eslint-disable fp/no-mutation */
import React from 'react'
import Settings from 'Settings'
import loadScript from '@daedalus/core/src/utils/network/loadScript'
import {v4 as uuid} from 'uuid'

import {logEvent} from 'services/datadogLogger'
import {
  BaseInitializationResponse,
  BoFHResponses,
  ChallengeResponse,
  FinalizationBookingResponse,
  Psd2Strategy,
  ServerCode,
  StatusCodes
} from 'services/psd2/types'
import {getBookingFinalizationLink} from 'business/bofhLinks'
import {BookingCreationResponse} from '@daedalus/core/src/booking/types/BookingCreationResponse'

declare let PayThreeDSConnector: unknown

// EPS Specific subtypes

interface EpsInitializationResponse extends BaseInitializationResponse {}

interface EpsSetupResponseDefaults {
  browserAcceptHeader: string
  merchantUrl: string
  preferredChallengeWindowSize: string
}

export interface EpsSetupResponse extends EpsSetupResponseDefaults {
  encodedBrowserMetadata: string
  version: string
}

interface ThreeDSConnectorSetupResponse {
  version: string
  encodedBrowserMetadata: string
}

interface ThreeDomainSecureParams {
  encodedChallengeConfig: string
  encodedInitConfig: string
  paymentSessionId: string
}

type EpsBookingCreationResponse =
  BookingCreationResponse<ThreeDomainSecureParams>

// Private
const _secureParams: EpsSetupResponseDefaults = {
  browserAcceptHeader: 'text/html,application/xml',
  merchantUrl: 'https://secure.findhotel.net',
  preferredChallengeWindowSize: 'full_screen'
}

const _bofhResponses: BoFHResponses<EpsBookingCreationResponse> = {
  initialization: undefined,
  challenge: undefined
}

// Public

let connector

const iframeRef = React.createRef<HTMLIFrameElement>()
const sessionId = uuid()

const beforeBooking = () => {
  const divContainer = iframeRef.current

  try {
    logEvent('services.PSD2.connector.InjectingIframe')
    if (divContainer.children.length === 0) {
      const iframe = document.createElement('iframe')
      iframe.src = Settings.get('REACT_APP_EPS_PSD2_IFRAME_URL')
      iframe.id = 'threedsiframe'
      iframe.width = '100%'
      iframe.height = '350'
      iframe.sandbox = 'allow-scripts allow-forms allow-same-origin'

      iframe.frameBorder = 0
      divContainer.appendChild(iframe)
    }
  } catch (error) {
    logEvent('services.PSD2.connector.InjectingIframeError', {meta: error})
  }
}

const setup = async () => {
  try {
    const scriptUrl =
      'https://static.pay.expedia.com/3ds/1.3.39/pay-3ds-js-libs-connector.min.js'
    logEvent('services.PSD2.connector.LoadingScript', {
      scriptUrl
    })
    await loadScript(scriptUrl, true)
  } catch (error) {
    logEvent('services.PSD2.connector.LoadingScriptError', {meta: error})
  }

  connector = new PayThreeDSConnector.ThreeDSConnector(
    'threedsiframe',
    'https://static.pay.expedia.com'
  )

  logEvent('services.PSD2.connector.ConnectorInstance', {meta: {connector}})

  const setupConfig = {
    referenceId: `${Settings.get('REACT_APP_EPS_PROFILE_KEY')}_${sessionId}`
  }

  logEvent('services.PSD2.connector.setup', {
    meta: setupConfig
  })
  const setupResult: ThreeDSConnectorSetupResponse = await connector.setup(
    setupConfig
  )

  const decoratedSecureParams: EpsSetupResponse = {
    ..._secureParams,
    ...setupResult
  }
  logEvent('services.PSD2.connector.setupResponse', {
    meta: decoratedSecureParams
  })
  return decoratedSecureParams
}

const initializeBooking = async (
  resp: EpsBookingCreationResponse
): Promise<EpsInitializationResponse> => {
  if (resp) _bofhResponses.initialization = resp

  logEvent('services.PSD2.initializedBookingResponse', {
    meta: _bofhResponses.initialization
  })

  const {paymentSessionId, encodedInitConfig} =
    _bofhResponses.initialization.threeDomainSecure.params
  logEvent('services.PSD2.connector.initSession', {
    meta: {
      paymentSessionId,
      encodedInitConfig
    }
  })
  const initSessionResponse: EpsInitializationResponse =
    await connector.initSession({
      paymentSessionId,
      encodedInitConfig
    })

  logEvent('services.PSD2.connector.initSessionResponse', {
    meta: initSessionResponse
  })

  return initSessionResponse
}

const challenge = async (): Promise<ChallengeResponse> => {
  const params = _bofhResponses.initialization
  const connectorChallengeConfig = {
    paymentSessionId: params.threeDomainSecure.params.paymentSessionId,
    encodedChallengeConfig:
      params.threeDomainSecure.params.encodedChallengeConfig
  }

  logEvent('services.PSD2.connector.challenge', {
    meta: connectorChallengeConfig
  })

  try {
    const response = await connector.challenge(connectorChallengeConfig)
    logEvent('services.PSD2.connector.challengeResponse', {
      meta: response
    })

    // We won't return ERROR as EPS asks us to always finish the payment process
    // regardless of the outcome. So for EPS the user will proceed to the booking-finalize
    // but get an error. After that the user might try again.
    return {
      status: StatusCodes.SUCCESS
    }
  } catch (error) {
    logEvent(`services.PSD2.${ServerCode.Eps}.AuthError`, error)
    return {
      status: StatusCodes.ERROR,
      error
    }
  }
}

const isEnrolled = (resp: EpsBookingCreationResponse) => {
  _bofhResponses.initialization = resp
  return (
    resp?.threeDomainSecure?.enrolled ||
    resp?.status === 'three_domain_secure_required'
  )
}

const finalizeBooking = async (): Promise<FinalizationBookingResponse> => {
  const params = _bofhResponses.initialization
  const bookingFinalizationLink = getBookingFinalizationLink(params.links)

  logEvent('services.PSD2.finalizeBooking', {
    meta: {bookingFinalizationLink}
  })

  return {
    success: true,
    link: bookingFinalizationLink
  }
}

const Psd2: Psd2Strategy<EpsSetupResponse, EpsBookingCreationResponse> = {
  setup,
  beforeBooking,
  initializeBooking,
  finalizeBooking,
  iframeRef,
  challenge,
  isEnrolled,
  serverCode: ServerCode.Eps
}

export default Psd2
